import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import VideocamIcon from "@mui/icons-material/Videocam";
import PropTypes from "prop-types";
import LoggedInLayout from "../../layout/LoggedInLayout";
import httpcommon from "../../helper/httpcommon";
import {
  cancelCounsellorAppointment,
  cancelSkillProviderAppointment,
  fetchCounsellorAppointments,
  fetchSkillProviderAppointments,
} from "../../services/api";

const NAVY = "#292E5C";
const ORANGE = "#F7AA47";
const INK = "#231F20";
const MUTED = "#808284";
const BORDER = "#E2ECF3";
const TARGET_YEAR = "2026";
const ASSESSMENT_INTERVAL_MONTHS = 3;

const CAREER_CARD_COLORS = ["#FBEAC8", "#C0E5E8", "#F4CCD9"];
const CAREER_IMAGES = [
  "/assets/newImages/INSPECTOR.png",
  "/assets/newImages/PROTECTOR.png",
  "/assets/newImages/ADVOCATE.png",
];

const EMPTY_SECTIONS = {
  skill_growth_plans: [],
  talk_to_career_buddy: [],
  own_targets: [],
};
const EMPTY_CAREER_OPTIONS = [];
const SECTIONS_WITH_ADD_SKILL = new Set([
  "skill_growth_plans",
  "talk_to_career_buddy",
  "own_targets",
]);

const styles = {
  page: {
    px: { xs: 2, md: 3 },
    pb: 8,
  },
  content: {
    width: "100%",
    maxWidth: 960,
    mx: "auto",
  },
  reportBar: {
    minHeight: 46,
    border: `1px solid ${BORDER}`,
    borderRadius: "7px",
    bgcolor: "#FAFBFC",
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    px: 2,
    color: INK,
  },
  reportIcon: {
    width: 28,
    height: 28,
    borderRadius: "7px",
    bgcolor: "rgba(247, 170, 71, 0.16)",
    color: ORANGE,
    display: "grid",
    placeItems: "center",
  },
  sectionTitle: {
    color: INK,
    fontSize: { xs: 15, md: 16 },
    fontWeight: 700,
    textAlign: "center",
  },
  careerGrid: {
    mt: 3,
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0, 1fr)",
      sm: "repeat(2, minmax(230px, 1fr))",
      lg: "repeat(3, minmax(230px, 1fr))",
    },
    gap: { xs: 2.5, md: 3 },
    justifyItems: "center",
  },
  careerCard: (active, bg) => ({
    width: "100%",
    maxWidth: 250,
    height: 375,
    position: "relative",
    cursor: "pointer",
    border: "none",
    background: "transparent",
    p: 0,
    textAlign: "center",
    font: "inherit",
    color: "inherit",
    transition: "transform 180ms ease",
    "&:hover": {
      transform: "translateY(-3px)",
    },
    "&:focus-visible": {
      outline: `3px solid ${ORANGE}`,
      outlineOffset: 4,
      borderRadius: "26px",
    },
    "& .career-color": {
      height: "100%",
      borderTopRightRadius: "40%",
      borderBottomLeftRadius: "40%",
      bgcolor: bg,
      boxShadow: active
        ? "0 0 0 3px rgba(247, 170, 71, 0.36), 0 16px 34px rgba(41, 46, 92, 0.16)"
        : "0 12px 28px rgba(41, 46, 92, 0.08)",
      transition: "box-shadow 180ms ease",
    },
  }),
  careerInner: {
    width: "84%",
    height: 290,
    bgcolor: "#fff",
    position: "absolute",
    top: 59,
    left: "8%",
    borderRadius: "20%",
    borderTopRightRadius: 0,
    px: 2,
    pt: 2.25,
    pb: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  },
  careerSelectionIndicator: (active) => ({
    position: "absolute",
    top: 12,
    right: 12,
    zIndex: 1,
    minWidth: active ? 82 : 22,
    height: 24,
    px: active ? 0.9 : 0,
    borderRadius: 999,
    bgcolor: active ? "rgba(247, 170, 71, 0.17)" : "#fff",
    border: `1px solid ${active ? ORANGE : BORDER}`,
    color: active ? NAVY : "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.6,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1,
    boxShadow: active
      ? "0 6px 14px rgba(247, 170, 71, 0.22)"
      : "0 4px 10px rgba(41, 46, 92, 0.08)",
    "&::before": {
      content: '""',
      width: 10,
      height: 10,
      borderRadius: "50%",
      border: `2px solid ${active ? ORANGE : "#B8C1D1"}`,
      bgcolor: active ? ORANGE : "#fff",
      boxShadow: active ? "inset 0 0 0 2px #fff" : "none",
      flexShrink: 0,
    },
  }),
  careerOptionLabel: (active) => ({
    position: "absolute",
    top: 26,
    left: 0,
    right: 0,
    color: active ? ORANGE : INK,
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    letterSpacing: 0,
  }),
  careerImage: {
    width: 86,
    height: 78,
    objectFit: "contain",
    mb: 1.8,
    mt: 1,
  },
  careerTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: INK,
    lineHeight: 1.35,
    minHeight: 41,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflowWrap: "anywhere",
  },
  careerDescription: {
    mt: 1,
    width: "100%",
    flex: 1,
    minHeight: 0,
    maxHeight: 111,
    pr: 0.7,
    color: MUTED,
    fontSize: 13,
    lineHeight: 1.65,
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(41, 46, 92, 0.36) transparent",
    overscrollBehavior: "contain",
    "&::-webkit-scrollbar": {
      width: 4,
    },
    "&::-webkit-scrollbar-track": {
      bgcolor: "rgba(226, 236, 243, 0.55)",
      borderRadius: 999,
    },
    "&::-webkit-scrollbar-thumb": {
      bgcolor: "rgba(41, 46, 92, 0.32)",
      borderRadius: 999,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      bgcolor: "rgba(41, 46, 92, 0.52)",
    },
  },
  assessmentBand: {
    mt: 3.5,
    height: 164,
    position: "relative",
    overflow: "hidden",
    borderRadius: "10px",
    background:
      "linear-gradient(90deg, #68BF92 0%, #97C895 42%, #FBC875 100%)",
  },
  assessmentContent: {
    position: "absolute",
    zIndex: 1,
    left: { xs: 24, sm: 150, md: 214 },
    top: { xs: 43, sm: 49 },
    display: "flex",
    alignItems: "flex-start",
    gap: { xs: 1.6, sm: 2 },
  },
  assessmentMark: {
    width: { xs: 78, sm: 88 },
    height: { xs: 68, sm: 76 },
    color: "#fff",
    flexShrink: 0,
  },
  assessmentSvg: {
    display: "block",
    width: "100%",
    height: "100%",
  },
  assessmentCopy: {
    pt: { xs: 0.3, sm: 0 },
    minWidth: { xs: 0, sm: 240 },
  },
  assessmentLabel: {
    color: INK,
    fontSize: { xs: 14, sm: 15 },
    fontWeight: 500,
    lineHeight: 1.25,
  },
  assessmentDate: {
    mt: 0.55,
    color: INK,
    fontSize: { xs: 21, sm: 24 },
    fontWeight: 800,
    lineHeight: 1.1,
    overflowWrap: "anywhere",
  },
  assessmentReminderButton: {
    mt: 0.75,
    minWidth: 0,
    bgcolor: NAVY,
    borderRadius: "7px",
    border: `1px solid ${BORDER}`,
    color: "#fff",
    textTransform: "none",
    fontFamily: "Montserrat",
    fontSize: 11,
    fontWeight: 500,
    lineHeight: "13.75px",
    px: "15px",
    py: "6px",
    boxShadow: "none",
    "&:hover": {
      bgcolor: "#20254D",
      boxShadow: "none",
    },
  },
  sparkle: ({ size, left, right, top, opacity = 0.82 }) => ({
    position: "absolute",
    zIndex: 0,
    width: size,
    height: size,
    left,
    right,
    top,
    bgcolor: "rgba(255, 255, 255, 0.88)",
    opacity,
    clipPath:
      "polygon(50% 0%, 62% 36%, 100% 50%, 62% 64%, 50% 100%, 38% 64%, 0% 50%, 38% 36%)",
  }),
  primaryButton: {
    bgcolor: NAVY,
    borderRadius: "7px",
    border: `1px solid ${BORDER}`,
    color: "#fff",
    textTransform: "none",
    fontSize: 12,
    fontWeight: 600,
    px: 2,
    py: 1,
    boxShadow: "none",
    "&:hover": {
      bgcolor: "#20254D",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      bgcolor: NAVY,
      color: "#fff",
      WebkitTextFillColor: "#fff",
      opacity: 1,
    },
  },
  skillProviderButton: {
    bgcolor: ORANGE,
    borderRadius: "7px",
    border: "1px solid rgba(247, 170, 71, 0.62)",
    color: INK,
    textTransform: "none",
    fontSize: 12,
    fontWeight: 700,
    px: 2.2,
    py: 1,
    boxShadow: "0 8px 18px rgba(247, 170, 71, 0.22)",
    "&:hover": {
      bgcolor: "#F4A13A",
      boxShadow: "0 10px 22px rgba(247, 170, 71, 0.28)",
    },
  },
  counsellorCta: {
    mt: 3.25,
    mb: 1.25,
    width: { xs: "100%", sm: 560, md: 640 },
    maxWidth: "100%",
    minHeight: { xs: 72, sm: 76 },
    mx: "auto",
    position: "relative",
    overflow: "visible",
    border: "none",
    borderRadius: "8px",
    bgcolor: "#D5F3F6",
    color: INK,
    textTransform: "none",
    fontFamily: "Montserrat",
    fontSize: { xs: 13.5, sm: 15 },
    fontWeight: 800,
    letterSpacing: 0,
    lineHeight: 1.25,
    boxShadow: "0 8px 0 rgba(35, 31, 32, 0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    px: { xs: 3.5, sm: 6 },
    "&:hover": {
      bgcolor: "#CDEFF2",
      boxShadow: "0 8px 0 rgba(35, 31, 32, 0.12)",
    },
    "&:focus-visible": {
      outline: `3px solid ${ORANGE}`,
      outlineOffset: 3,
    },
  },
  counsellorIconWrap: {
    position: "absolute",
    left: { xs: 20, sm: 30 },
    top: { xs: -18, sm: -21 },
    width: { xs: 48, sm: 52 },
    height: { xs: 48, sm: 52 },
    display: "grid",
    placeItems: "center",
  },
  counsellorBellIcon: {
    color: "#4A4A4A",
    fontSize: { xs: 43, sm: 46 },
    strokeWidth: 1,
  },
  counsellorPlus: {
    position: "absolute",
    right: -6,
    top: 5,
    width: 18,
    height: 18,
    borderRadius: "50%",
    bgcolor: "#FF2F5F",
    border: "2px solid #fff",
    color: "#fff",
    fontSize: 15,
    fontWeight: 800,
    lineHeight: "14px",
    display: "grid",
    placeItems: "center",
  },
  counsellorText: {
    width: "100%",
    color: INK,
    fontSize: "inherit",
    fontWeight: 800,
    lineHeight: 1.25,
    textAlign: "center",
    overflowWrap: "anywhere",
  },
  appointmentsPanel: {
    mt: 3,
    mb: 3.5,
    p: 1.25,
    bgcolor: "rgba(239, 239, 239, 0.40)",
    borderRadius: "7px",
    border: "1px solid rgba(226, 236, 243, 0.72)",
    display: "flex",
    flexDirection: "column",
    gap: 1.25,
  },
  appointmentsHeader: {
    display: "flex",
    alignItems: { xs: "stretch", sm: "center" },
    justifyContent: "space-between",
    flexDirection: { xs: "column", sm: "row" },
    gap: 1.25,
  },
  appointmentsTitle: {
    color: INK,
    fontSize: 18,
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 500,
    lineHeight: "24px",
  },
  appointmentsSearch: {
    width: { xs: "100%", sm: 245 },
    "& .MuiOutlinedInput-root": {
      height: 36,
      borderRadius: "7px",
      bgcolor: "#fff",
      fontSize: 12,
      fontFamily: "Montserrat, sans-serif",
      color: INK,
      boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
      "& fieldset": {
        borderColor: BORDER,
      },
      "&:hover fieldset": {
        borderColor: "#C8D8E4",
      },
      "&.Mui-focused fieldset": {
        borderColor: NAVY,
        borderWidth: 1,
      },
    },
    "& input": {
      py: 0,
      "&::placeholder": {
        color: "#888888",
        fontFamily: "Montserrat, sans-serif",
        fontSize: 12,
        fontWeight: 400,
        lineHeight: "15px",
        opacity: 1,
      },
    },
  },
  appointmentsTableWrap: {
    bgcolor: "#fff",
    borderRadius: "7px",
    border: "1px solid #DCDFDE",
    overflowX: "auto",
    boxShadow: "0 8px 24px rgba(41, 46, 92, 0.05)",
  },
  appointmentsTable: {
    minWidth: 900,
    tableLayout: "fixed",
  },
  appointmentHeadCell: {
    height: 44,
    bgcolor: "#F9FAFB",
    color: "#667085",
    fontSize: 12,
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
    lineHeight: "18px",
    borderBottom: "1px solid #EAECF0",
    py: 1.25,
    px: 1.25,
    whiteSpace: "nowrap",
  },
  appointmentSortableLabel: {
    color: "#667085",
    fontSize: 12,
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
    lineHeight: "18px",
    "&:hover": {
      color: NAVY,
    },
    "&.Mui-active": {
      color: NAVY,
    },
    "& .MuiTableSortLabel-icon": {
      fontSize: 16,
      color: "#6E6893 !important",
      opacity: 1,
      ml: 0.3,
    },
  },
  appointmentCell: {
    height: 72,
    color: "#344054",
    fontSize: 13,
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
    lineHeight: "18px",
    borderBottom: "1px solid #EAECF0",
    py: 1.5,
    px: 1.25,
    overflowWrap: "anywhere",
    verticalAlign: "middle",
  },
  appointmentRow: {
    transition: "background-color 160ms ease",
    "&:hover": {
      bgcolor: "#FAFBFF",
    },
    "&:last-of-type .MuiTableCell-root": {
      borderBottom: "none",
    },
  },
  appointmentIndexCell: {
    width: 72,
    color: "#101828",
    textAlign: "center",
  },
  appointmentDateCell: {
    width: 128,
    textAlign: "center",
  },
  appointmentTimeCell: {
    width: 158,
    textAlign: "center",
  },
  appointmentCounsellorCell: {
    width: 186,
    color: "#101828",
  },
  appointmentStatusCell: {
    width: 116,
    textAlign: "center",
  },
  appointmentMeetCell: {
    width: 110,
    textAlign: "center",
  },
  appointmentActionCell: {
    width: 130,
    textAlign: "center",
  },
  appointmentStatusChip: (status) => {
    const statusColors = {
      confirmed: {
        bgcolor: "#ECFDF3",
        borderColor: "#ABEFC6",
        color: "#067647",
      },
      pending: {
        bgcolor: "#FFFAEB",
        borderColor: "#FEDF89",
        color: "#B54708",
      },
      cancelled: {
        bgcolor: "#F3F4F6",
        borderColor: "#D1D5DB",
        color: "rgba(17, 24, 39, 0.70)",
      },
      failed: {
        bgcolor: "#FEF3F2",
        borderColor: "#FECDCA",
        color: "#B42318",
      },
    };
    const selected = statusColors[status] || statusColors.cancelled;

    return {
      height: 24,
      px: 0.5,
      borderRadius: 999,
      bgcolor: selected.bgcolor,
      border: `1px solid ${selected.borderColor}`,
      color: selected.color,
      fontSize: 11,
      fontFamily: "Inter, sans-serif",
      fontWeight: 500,
      lineHeight: "16px",
      textTransform: "capitalize",
      "& .MuiChip-label": {
        px: 1,
      },
    };
  },
  meetIconButton: {
    width: 34,
    height: 34,
    color: NAVY,
    border: `1px solid ${BORDER}`,
    bgcolor: "#fff",
    boxShadow: "0 4px 10px rgba(41, 46, 92, 0.06)",
    "&:hover": {
      bgcolor: "rgba(41, 46, 92, 0.06)",
      borderColor: NAVY,
      boxShadow: "0 6px 14px rgba(41, 46, 92, 0.10)",
    },
    "&.Mui-disabled": {
      color: "#B8C1D1",
      borderColor: "#E5E7EB",
      bgcolor: "#F9FAFB",
      boxShadow: "none",
    },
  },
  cancelButton: {
    minWidth: 88,
    height: 34,
    borderRadius: "7px",
    color: "#B42318",
    borderColor: "#FDA29B",
    textTransform: "none",
    fontSize: 12,
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    px: 1.25,
    "&:hover": {
      borderColor: "#F04438",
      bgcolor: "#FEF3F2",
    },
  },
  appointmentsEmpty: {
    minHeight: 120,
    display: "grid",
    placeItems: "center",
    color: MUTED,
    fontSize: 13,
    fontFamily: "Poppins, sans-serif",
    textAlign: "center",
    px: 2,
  },
  appointmentsPagination: {
    borderTop: "1px solid #EAECF0",
    "& .MuiTablePagination-toolbar": {
      minHeight: 46,
      px: 1.25,
      fontFamily: "Montserrat, sans-serif",
      fontSize: 12,
      color: "#292948",
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      fontSize: 12,
      fontFamily: "Montserrat, sans-serif",
      fontWeight: 600,
      letterSpacing: "0.04em",
      color: "#292948",
    },
    "& .MuiTablePagination-spacer": {
      flex: 1,
    },
  },
  paginationActions: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.6,
    ml: 1,
  },
  paginationIconButton: {
    width: 32,
    height: 32,
    color: "#6E6893",
    "&:hover": {
      bgcolor: "rgba(41, 46, 92, 0.06)",
    },
    "&.Mui-disabled": {
      color: "#C9CBD8",
    },
  },
  paginationPagePill: {
    minWidth: 32,
    height: 32,
    borderRadius: 999,
    bgcolor: NAVY,
    color: "#F1EEF9",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 500,
    lineHeight: "26px",
    letterSpacing: "0.02px",
  },
  milestoneBand: {
    mt: 3.5,
    border: `1px solid ${BORDER}`,
    borderRadius: "7px",
    bgcolor: "rgba(239, 239, 239, 0.40)",
    px: { xs: 2, md: 2.5 },
    py: 2.75,
  },
  skillGrid: {
    mt: 2.5,
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0, 1fr)",
      sm: "repeat(2, minmax(190px, 1fr))",
      md: "repeat(4, minmax(170px, 1fr))",
    },
    gap: { xs: 2.5, md: 2 },
  },
  skillCard: (accent) => ({
    minHeight: 184,
    borderRadius: "18px",
    border: `1px solid ${BORDER}`,
    bgcolor: "#fff",
    position: "relative",
    overflow: "hidden",
    px: 1.6,
    pt: 2,
    pb: 1.4,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "space-between",
    gap: 1.15,
    boxShadow: "0 10px 26px rgba(41, 46, 92, 0.07)",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: "0 0 auto 0",
      height: 6,
      bgcolor: accent,
    },
  }),
  skillBadge: (accent) => ({
    width: 38,
    height: 38,
    borderRadius: "50%",
    bgcolor: `${accent}22`,
    color: accent,
    display: "grid",
    placeItems: "center",
    mb: 0.8,
  }),
  skillName: {
    color: INK,
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.35,
    overflowWrap: "anywhere",
  },
  skillMeta: {
    mt: 0.7,
    color: MUTED,
    fontSize: 12,
    lineHeight: 1.35,
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflowWrap: "anywhere",
  },
  ownTargetSuggestion: {
    mt: 0.8,
    color: NAVY,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.45,
    overflowWrap: "anywhere",
  },
  targetText: {
    color: INK,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.3,
  },
  emptyState: {
    mt: 2.5,
    minHeight: 112,
    border: `1px dashed ${BORDER}`,
    borderRadius: "7px",
    bgcolor: "#fff",
    display: "grid",
    placeItems: "center",
    px: 2,
    textAlign: "center",
  },
};

const getCareerKey = (career) =>
  career?.priority !== null && career?.priority !== undefined
    ? `priority-${career.priority}`
    : `career-${career?.career_choice || ""}`;

const getCareerOptionNumber = (career, fallbackIndex) => {
  if (career?.priority !== null && career?.priority !== undefined) {
    return Number(career.priority) + 1;
  }
  return fallbackIndex + 1;
};

const getDisplayLabel = (item, sectionKey) => {
  if (sectionKey === "skill_growth_plans") {
    return item?.suggestion_label || item?.improvement_label || "Skill";
  }
  return item?.improvement_label || item?.suggestion_label || "Skill";
};

const getMetaLabel = (item, sectionKey) => {
  if (sectionKey === "skill_growth_plans" && item?.improvement_label) {
    return item.improvement_label;
  }
  if (sectionKey === "talk_to_career_buddy") {
    return "Talk to Career Buddy";
  }
  return item?.area_label || "";
};

const getOwnTargetSuggestionLabel = (item) =>
  item?.suggestion_label || item?.suggestion || "";

const getErrorMessage = (error) =>
  error?.response?.data?.error ||
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.message ||
  "Unable to load milestone data.";

const formatAppointmentDate = (value, timezone = "Asia/Kolkata") => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: timezone,
  })
    .format(date)
    .replace(/\//g, "-");
};

const getOrdinalSuffix = (day) => {
  if (day >= 11 && day <= 13) return "th";

  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const addCalendarMonths = (date, months) => {
  const nextDate = new Date(date);
  const originalDay = nextDate.getDate();

  nextDate.setDate(1);
  nextDate.setMonth(nextDate.getMonth() + months);

  const lastDayOfTargetMonth = new Date(
    nextDate.getFullYear(),
    nextDate.getMonth() + 1,
    0
  ).getDate();

  nextDate.setDate(Math.min(originalDay, lastDayOfTargetMonth));
  return nextDate;
};

const formatAssessmentDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "-";

  const day = date.getDate();
  const month = new Intl.DateTimeFormat("en-IN", { month: "long" }).format(date);
  const year = date.getFullYear();

  return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
};

const getNextAssessmentDate = (milestone) => {
  const baseDateValue =
    milestone?.report?.created_at ||
    milestone?.report?.updated_at ||
    milestone?.created_at ||
    milestone?.updated_at;

  if (!baseDateValue) return "-";

  const baseDate = new Date(baseDateValue);
  if (Number.isNaN(baseDate.getTime())) return "-";

  return formatAssessmentDate(
    addCalendarMonths(baseDate, ASSESSMENT_INTERVAL_MONTHS)
  );
};

const formatAppointmentTime = (startAt, endAt, timezone = "Asia/Kolkata") => {
  if (!startAt) return "-";
  const startDate = new Date(startAt);
  const endDate = endAt ? new Date(endAt) : null;
  if (Number.isNaN(startDate.getTime())) return "-";

  const formatter = new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: timezone,
  });
  const start = formatter.format(startDate);
  const end =
    endDate && !Number.isNaN(endDate.getTime()) ? formatter.format(endDate) : "";

  return end ? `${start} - ${end}` : start;
};

const getStatusLabel = (status = "") =>
  String(status || "-")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const appointmentMatchesSearch = (appointment, searchTerm) => {
  const query = searchTerm.trim().toLowerCase();
  if (!query) return true;

  return [
    appointment.counsellor_name,
    appointment.status,
    appointment.student_email,
    appointment.counsellor_email,
    formatAppointmentDate(appointment.start_at, appointment.timezone),
    formatAppointmentTime(
      appointment.start_at,
      appointment.end_at,
      appointment.timezone
    ),
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query));
};

const skillProviderAppointmentMatchesSearch = (appointment, searchTerm) => {
  const query = searchTerm.trim().toLowerCase();
  if (!query) return true;

  return [
    appointment.skill_provider_name,
    appointment.skill_name,
    appointment.status,
    appointment.student_email,
    appointment.skill_provider_email,
    formatAppointmentDate(appointment.start_at, appointment.timezone),
    formatAppointmentTime(
      appointment.start_at,
      appointment.end_at,
      appointment.timezone
    ),
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query));
};

const getAppointmentSortValue = (appointment, sortBy) => {
  if (sortBy === "date" || sortBy === "time") {
    const date = new Date(appointment.start_at);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  if (sortBy === "counsellor") {
    return String(appointment.counsellor_name || "").toLowerCase();
  }

  return "";
};

const getSkillProviderAppointmentSortValue = (appointment, sortBy) => {
  if (sortBy === "date" || sortBy === "time") {
    const date = new Date(appointment.start_at);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  if (sortBy === "provider") {
    return String(appointment.skill_provider_name || "").toLowerCase();
  }

  return "";
};

const sortAppointments = (appointments, sortBy, sortDirection) => {
  const direction = sortDirection === "asc" ? 1 : -1;

  return [...appointments].sort((leftAppointment, rightAppointment) => {
    const left = getAppointmentSortValue(leftAppointment, sortBy);
    const right = getAppointmentSortValue(rightAppointment, sortBy);

    if (left < right) return -1 * direction;
    if (left > right) return 1 * direction;
    return (rightAppointment.id || 0) - (leftAppointment.id || 0);
  });
};

const sortSkillProviderAppointments = (appointments, sortBy, sortDirection) => {
  const direction = sortDirection === "asc" ? 1 : -1;

  return [...appointments].sort((leftAppointment, rightAppointment) => {
    const left = getSkillProviderAppointmentSortValue(leftAppointment, sortBy);
    const right = getSkillProviderAppointmentSortValue(rightAppointment, sortBy);

    if (left < right) return -1 * direction;
    if (left > right) return 1 * direction;
    return (rightAppointment.id || 0) - (leftAppointment.id || 0);
  });
};

const AppointmentPaginationActions = ({
  count,
  onPageChange,
  page,
  rowsPerPage,
}) => {
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  return (
    <Box sx={styles.paginationActions}>
      <IconButton
        type="button"
        onClick={(event) => onPageChange(event, page - 1)}
        disabled={page === 0}
        sx={styles.paginationIconButton}
        aria-label="Previous appointments page"
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      <Box sx={styles.paginationPagePill}>{page + 1}</Box>
      <IconButton
        type="button"
        onClick={(event) => onPageChange(event, page + 1)}
        disabled={page >= lastPage}
        sx={styles.paginationIconButton}
        aria-label="Next appointments page"
      >
        <KeyboardArrowRightIcon />
      </IconButton>
    </Box>
  );
};

AppointmentPaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const AssessmentBannerIcon = () => (
  <Box
    component="svg"
    viewBox="0 0 96 78"
    aria-hidden="true"
    focusable="false"
    sx={styles.assessmentSvg}
  >
    <path
      d="M26 48V10a4 4 0 0 1 4-4h48a4 4 0 0 1 4 4v38"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="4"
    />
    <path
      d="M34 38 46 27l11 9 18-20"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="5"
    />
    <path
      d="M63 16h12v12"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="5"
    />
    {[13, 31, 49, 67, 85].map((cx) => (
      <circle key={`head-${cx}`} cx={cx} cy="51" r="7" fill="currentColor" />
    ))}
    {[13, 31, 49, 67, 85].map((cx) => (
      <path
        key={`body-${cx}`}
        d={`M${cx - 11} 75c1-10 6-16 11-16s10 6 11 16`}
        fill="currentColor"
      />
    ))}
    <path
      d="M3 75h90"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="5"
    />
  </Box>
);

const Milestone = ({ studentUserId, studentName, childSelector }) => {
  const navigate = useNavigate();
  const [milestone, setMilestone] = useState(null);
  const [selectedCareerKey, setSelectedCareerKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState("");
  const [appointmentSearch, setAppointmentSearch] = useState("");
  const [appointmentPage, setAppointmentPage] = useState(0);
  const [appointmentsRowsPerPage, setAppointmentsRowsPerPage] = useState(5);
  const [appointmentSortBy, setAppointmentSortBy] = useState("date");
  const [appointmentSortDirection, setAppointmentSortDirection] = useState("desc");
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState(null);
  const [skillProviderAppointments, setSkillProviderAppointments] = useState([]);
  const [skillProviderAppointmentsLoading, setSkillProviderAppointmentsLoading] =
    useState(false);
  const [skillProviderAppointmentsError, setSkillProviderAppointmentsError] =
    useState("");
  const [skillProviderAppointmentSearch, setSkillProviderAppointmentSearch] =
    useState("");
  const [skillProviderAppointmentPage, setSkillProviderAppointmentPage] =
    useState(0);
  const [
    skillProviderAppointmentsRowsPerPage,
    setSkillProviderAppointmentsRowsPerPage,
  ] = useState(5);
  const [skillProviderAppointmentSortBy, setSkillProviderAppointmentSortBy] =
    useState("date");
  const [
    skillProviderAppointmentSortDirection,
    setSkillProviderAppointmentSortDirection,
  ] = useState("desc");
  const [
    cancellingSkillProviderAppointmentId,
    setCancellingSkillProviderAppointmentId,
  ] = useState(null);

  const fetchMilestone = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await httpcommon.get("school/student/milestone/", {
        params: studentUserId ? { student_user_id: studentUserId } : undefined,
      });
      const payload = response?.data || {};
      const defaultCareer =
        payload.selected_career || payload.career_options?.[0] || null;

      setMilestone(payload);
      setSelectedCareerKey(getCareerKey(defaultCareer));
    } catch (fetchError) {
      setMilestone(null);
      setSelectedCareerKey("");
      setError(getErrorMessage(fetchError));
    } finally {
      setLoading(false);
    }
  }, [studentUserId]);

  const loadAppointments = useCallback(async () => {
    setAppointmentsLoading(true);
    setAppointmentsError("");

    try {
      const response = await fetchCounsellorAppointments(studentUserId);
      const appointmentData = response?.data?.data;
      setAppointments(Array.isArray(appointmentData) ? appointmentData : []);
    } catch (appointmentError) {
      setAppointments([]);
      setAppointmentsError(getErrorMessage(appointmentError));
    } finally {
      setAppointmentsLoading(false);
    }
  }, [studentUserId]);

  const loadSkillProviderAppointments = useCallback(async () => {
    setSkillProviderAppointmentsLoading(true);
    setSkillProviderAppointmentsError("");

    try {
      const response = await fetchSkillProviderAppointments(studentUserId);
      const appointmentData = response?.data?.data;
      setSkillProviderAppointments(
        Array.isArray(appointmentData) ? appointmentData : []
      );
    } catch (appointmentError) {
      setSkillProviderAppointments([]);
      setSkillProviderAppointmentsError(getErrorMessage(appointmentError));
    } finally {
      setSkillProviderAppointmentsLoading(false);
    }
  }, [studentUserId]);

  useEffect(() => {
    fetchMilestone();
  }, [fetchMilestone]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    loadSkillProviderAppointments();
  }, [loadSkillProviderAppointments]);

  useEffect(() => {
    setAppointmentPage(0);
  }, [appointmentSearch, appointmentSortBy, appointmentSortDirection]);

  useEffect(() => {
    setSkillProviderAppointmentPage(0);
  }, [
    skillProviderAppointmentSearch,
    skillProviderAppointmentSortBy,
    skillProviderAppointmentSortDirection,
  ]);

  const careerOptions = milestone?.career_options || EMPTY_CAREER_OPTIONS;

  const selectedCareer = useMemo(() => {
    if (!careerOptions.length) return null;
    return (
      careerOptions.find((career) => getCareerKey(career) === selectedCareerKey) ||
      careerOptions[0]
    );
  }, [careerOptions, selectedCareerKey]);

  const sections = selectedCareer?.sections || milestone?.sections || EMPTY_SECTIONS;
  const nextAssessmentDate = useMemo(
    () => getNextAssessmentDate(milestone),
    [milestone]
  );

  const filteredAppointments = useMemo(
    () =>
      appointments.filter((appointment) =>
        appointmentMatchesSearch(appointment, appointmentSearch)
      ),
    [appointments, appointmentSearch]
  );

  const sortedAppointments = useMemo(
    () =>
      sortAppointments(
        filteredAppointments,
        appointmentSortBy,
        appointmentSortDirection
      ),
    [appointmentSortBy, appointmentSortDirection, filteredAppointments]
  );

  const visibleAppointments = useMemo(() => {
    const start = appointmentPage * appointmentsRowsPerPage;
    return sortedAppointments.slice(start, start + appointmentsRowsPerPage);
  }, [appointmentPage, appointmentsRowsPerPage, sortedAppointments]);

  const filteredSkillProviderAppointments = useMemo(
    () =>
      skillProviderAppointments.filter((appointment) =>
        skillProviderAppointmentMatchesSearch(
          appointment,
          skillProviderAppointmentSearch
        )
      ),
    [skillProviderAppointments, skillProviderAppointmentSearch]
  );

  const sortedSkillProviderAppointments = useMemo(
    () =>
      sortSkillProviderAppointments(
        filteredSkillProviderAppointments,
        skillProviderAppointmentSortBy,
        skillProviderAppointmentSortDirection
      ),
    [
      filteredSkillProviderAppointments,
      skillProviderAppointmentSortBy,
      skillProviderAppointmentSortDirection,
    ]
  );

  const visibleSkillProviderAppointments = useMemo(() => {
    const start =
      skillProviderAppointmentPage * skillProviderAppointmentsRowsPerPage;
    return sortedSkillProviderAppointments.slice(
      start,
      start + skillProviderAppointmentsRowsPerPage
    );
  }, [
    skillProviderAppointmentPage,
    skillProviderAppointmentsRowsPerPage,
    sortedSkillProviderAppointments,
  ]);

  const handleAppointmentSort = (sortBy) => {
    if (appointmentSortBy === sortBy) {
      setAppointmentSortDirection((currentDirection) =>
        currentDirection === "asc" ? "desc" : "asc"
      );
      return;
    }

    setAppointmentSortBy(sortBy);
    setAppointmentSortDirection(sortBy === "counsellor" ? "asc" : "desc");
  };

  const handleSkillProviderAppointmentSort = (sortBy) => {
    if (skillProviderAppointmentSortBy === sortBy) {
      setSkillProviderAppointmentSortDirection((currentDirection) =>
        currentDirection === "asc" ? "desc" : "asc"
      );
      return;
    }

    setSkillProviderAppointmentSortBy(sortBy);
    setSkillProviderAppointmentSortDirection(
      sortBy === "provider" ? "asc" : "desc"
    );
  };

  const handleCancelAppointment = async (appointment) => {
    if (!appointment?.id || !appointment.can_cancel) return;

    const shouldCancel = window.confirm(
      "Cancel this counselling appointment? The slot will become available again."
    );
    if (!shouldCancel) return;

    setCancellingAppointmentId(appointment.id);
    setAppointmentsError("");

    try {
      const response = await cancelCounsellorAppointment(
        appointment.id,
        studentUserId,
      );
      await loadAppointments();

      if (response?.data?.warning) {
        setAppointmentsError(response.data.message || response.data.warning);
      }
    } catch (cancelError) {
      setAppointmentsError(getErrorMessage(cancelError));
    } finally {
      setCancellingAppointmentId(null);
    }
  };

  const handleCancelSkillProviderAppointment = async (appointment) => {
    if (!appointment?.id || !appointment.can_cancel) return;

    const shouldCancel = window.confirm(
      "Cancel this skill provider appointment? The slot will become available again."
    );
    if (!shouldCancel) return;

    setCancellingSkillProviderAppointmentId(appointment.id);
    setSkillProviderAppointmentsError("");

    try {
      const response = await cancelSkillProviderAppointment(
        appointment.id,
        studentUserId,
      );
      await loadSkillProviderAppointments();

      if (response?.data?.warning) {
        setSkillProviderAppointmentsError(
          response.data.message || response.data.warning
        );
      }
    } catch (cancelError) {
      setSkillProviderAppointmentsError(getErrorMessage(cancelError));
    } finally {
      setCancellingSkillProviderAppointmentId(null);
    }
  };

  const renderCareerCards = () => (
    <Box sx={styles.careerGrid}>
      {careerOptions.map((career, index) => {
        const active = getCareerKey(career) === getCareerKey(selectedCareer);
        const optionNumber = getCareerOptionNumber(career, index);
        const bg = CAREER_CARD_COLORS[index % CAREER_CARD_COLORS.length];
        const image = CAREER_IMAGES[index % CAREER_IMAGES.length];

        return (
          <Box
            component="button"
            key={getCareerKey(career)}
            type="button"
            aria-pressed={active}
            sx={styles.careerCard(active, bg)}
            onClick={() => setSelectedCareerKey(getCareerKey(career))}
          >
            <Box className="career-color" />
            <Typography sx={styles.careerOptionLabel(active)}>
              Career Option {optionNumber}
            </Typography>
            <Box sx={styles.careerInner}>
              <Box aria-hidden="true" sx={styles.careerSelectionIndicator(active)}>
                {active ? "Selected" : ""}
              </Box>
              <Box component="img" src={image} alt="" sx={styles.careerImage} />
              <Typography sx={styles.careerTitle}>
                {career.career_label || career.career_choice || "Career"}
              </Typography>
              <Typography sx={styles.careerDescription}>
                {career.short_description || career.description || "-"}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const renderAppointmentsTable = () => (
    <Box sx={styles.appointmentsPanel}>
      <Box sx={styles.appointmentsHeader}>
        <Typography sx={styles.appointmentsTitle}>Career Counsellors</Typography>
        <TextField
          value={appointmentSearch}
          onChange={(event) => setAppointmentSearch(event.target.value)}
          placeholder="Search..."
          size="small"
          sx={styles.appointmentsSearch}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: "#585874", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {appointmentsError ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={loadAppointments}>
              Retry
            </Button>
          }
        >
          {appointmentsError}
        </Alert>
      ) : null}

      <Box sx={styles.appointmentsTableWrap}>
        {appointmentsLoading ? (
          <Box sx={styles.appointmentsEmpty}>
            <CircularProgress size={28} sx={{ color: NAVY }} />
          </Box>
        ) : filteredAppointments.length ? (
          <>
            <TableContainer>
              <Table
                sx={styles.appointmentsTable}
                aria-label="Career counsellor appointments"
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentIndexCell,
                        color: "#667085",
                      }}
                    >
                      Sr No.
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentDateCell,
                      }}
                    >
                      <TableSortLabel
                        active={appointmentSortBy === "date"}
                        direction={
                          appointmentSortBy === "date"
                            ? appointmentSortDirection
                            : "desc"
                        }
                        onClick={() => handleAppointmentSort("date")}
                        sx={styles.appointmentSortableLabel}
                      >
                        Meeting dates
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentTimeCell,
                      }}
                    >
                      <TableSortLabel
                        active={appointmentSortBy === "time"}
                        direction={
                          appointmentSortBy === "time"
                            ? appointmentSortDirection
                            : "desc"
                        }
                        onClick={() => handleAppointmentSort("time")}
                        sx={styles.appointmentSortableLabel}
                      >
                        Meeting time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentCounsellorCell,
                      }}
                    >
                      <TableSortLabel
                        active={appointmentSortBy === "counsellor"}
                        direction={
                          appointmentSortBy === "counsellor"
                            ? appointmentSortDirection
                            : "asc"
                        }
                        onClick={() => handleAppointmentSort("counsellor")}
                        sx={styles.appointmentSortableLabel}
                      >
                        Counsellor name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentStatusCell,
                      }}
                    >
                      <Box component="span" sx={styles.appointmentSortableLabel}>
                        Status
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentMeetCell,
                      }}
                    >
                      Google Meet
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentActionCell,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleAppointments.map((appointment, index) => {
                    const rowNumber =
                      appointmentPage * appointmentsRowsPerPage + index + 1;
                    const meetLink = appointment.google_meet_link || "";
                    const isCancelling =
                      cancellingAppointmentId === appointment.id;

                    return (
                      <TableRow key={appointment.id} sx={styles.appointmentRow}>
                        <TableCell
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentIndexCell,
                          }}
                        >
                          {rowNumber}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentDateCell,
                          }}
                        >
                          {formatAppointmentDate(
                            appointment.start_at,
                            appointment.timezone
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentTimeCell,
                          }}
                        >
                          {formatAppointmentTime(
                            appointment.start_at,
                            appointment.end_at,
                            appointment.timezone
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentCounsellorCell,
                          }}
                        >
                          {appointment.counsellor_name || "Counsellor"}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentStatusCell,
                          }}
                        >
                          <Chip
                            label={getStatusLabel(appointment.status)}
                            sx={styles.appointmentStatusChip(appointment.status)}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentMeetCell,
                          }}
                        >
                          <Tooltip
                            title={
                              meetLink
                                ? "Open Google Meet"
                                : "Meet link not available"
                            }
                          >
                            <Box component="span">
                              <IconButton
                                component={meetLink ? "a" : "button"}
                                href={meetLink || undefined}
                                target={meetLink ? "_blank" : undefined}
                                rel={meetLink ? "noopener noreferrer" : undefined}
                                disabled={!meetLink}
                                sx={styles.meetIconButton}
                                aria-label="Open Google Meet"
                              >
                                <VideocamIcon sx={{ fontSize: 19 }} />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentActionCell,
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={!appointment.can_cancel || isCancelling}
                            onClick={() => handleCancelAppointment(appointment)}
                            startIcon={
                              isCancelling ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <CancelOutlinedIcon sx={{ fontSize: 16 }} />
                              )
                            }
                            sx={styles.cancelButton}
                          >
                            Cancel
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredAppointments.length}
              page={appointmentPage}
              onPageChange={(event, nextPage) => setAppointmentPage(nextPage)}
              rowsPerPage={appointmentsRowsPerPage}
              onRowsPerPageChange={(event) => {
                setAppointmentsRowsPerPage(Number(event.target.value));
                setAppointmentPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Rows"
              labelDisplayedRows={({ count }) => `Total - ${count}`}
              ActionsComponent={AppointmentPaginationActions}
              sx={styles.appointmentsPagination}
            />
          </>
        ) : (
          <Box sx={styles.appointmentsEmpty}>
            {appointmentSearch
              ? "No counsellor bookings match your search."
              : "No counsellor bookings found yet."}
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderSkillProviderAppointmentsTable = () => (
    <Box sx={styles.appointmentsPanel}>
      <Box sx={styles.appointmentsHeader}>
        <Typography sx={styles.appointmentsTitle}>Skill Providers</Typography>
        <TextField
          value={skillProviderAppointmentSearch}
          onChange={(event) =>
            setSkillProviderAppointmentSearch(event.target.value)
          }
          placeholder="Search..."
          size="small"
          sx={styles.appointmentsSearch}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: "#585874", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {skillProviderAppointmentsError ? (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={loadSkillProviderAppointments}
            >
              Retry
            </Button>
          }
        >
          {skillProviderAppointmentsError}
        </Alert>
      ) : null}

      <Box sx={styles.appointmentsTableWrap}>
        {skillProviderAppointmentsLoading ? (
          <Box sx={styles.appointmentsEmpty}>
            <CircularProgress size={28} sx={{ color: NAVY }} />
          </Box>
        ) : filteredSkillProviderAppointments.length ? (
          <>
            <TableContainer>
              <Table
                sx={styles.appointmentsTable}
                aria-label="Skill provider appointments"
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentIndexCell,
                        color: "#667085",
                      }}
                    >
                      Sr No.
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentDateCell,
                      }}
                    >
                      <TableSortLabel
                        active={skillProviderAppointmentSortBy === "date"}
                        direction={
                          skillProviderAppointmentSortBy === "date"
                            ? skillProviderAppointmentSortDirection
                            : "desc"
                        }
                        onClick={() => handleSkillProviderAppointmentSort("date")}
                        sx={styles.appointmentSortableLabel}
                      >
                        Meeting dates
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentTimeCell,
                      }}
                    >
                      <TableSortLabel
                        active={skillProviderAppointmentSortBy === "time"}
                        direction={
                          skillProviderAppointmentSortBy === "time"
                            ? skillProviderAppointmentSortDirection
                            : "desc"
                        }
                        onClick={() => handleSkillProviderAppointmentSort("time")}
                        sx={styles.appointmentSortableLabel}
                      >
                        Meeting time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentCounsellorCell,
                      }}
                    >
                      <TableSortLabel
                        active={skillProviderAppointmentSortBy === "provider"}
                        direction={
                          skillProviderAppointmentSortBy === "provider"
                            ? skillProviderAppointmentSortDirection
                            : "asc"
                        }
                        onClick={() =>
                          handleSkillProviderAppointmentSort("provider")
                        }
                        sx={styles.appointmentSortableLabel}
                      >
                        Skill provider name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentStatusCell,
                      }}
                    >
                      <Box component="span" sx={styles.appointmentSortableLabel}>
                        Status
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentMeetCell,
                      }}
                    >
                      Google Meet
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...styles.appointmentHeadCell,
                        ...styles.appointmentActionCell,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleSkillProviderAppointments.map((appointment, index) => {
                    const rowNumber =
                      skillProviderAppointmentPage *
                        skillProviderAppointmentsRowsPerPage +
                      index +
                      1;
                    const meetLink = appointment.google_meet_link || "";
                    const isCancelling =
                      cancellingSkillProviderAppointmentId === appointment.id;

                    return (
                      <TableRow key={appointment.id} sx={styles.appointmentRow}>
                        <TableCell
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentIndexCell,
                          }}
                        >
                          {rowNumber}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentDateCell,
                          }}
                        >
                          {formatAppointmentDate(
                            appointment.start_at,
                            appointment.timezone
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentTimeCell,
                          }}
                        >
                          {formatAppointmentTime(
                            appointment.start_at,
                            appointment.end_at,
                            appointment.timezone
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentCounsellorCell,
                          }}
                        >
                          {appointment.skill_provider_name || "Skill Provider"}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentStatusCell,
                          }}
                        >
                          <Chip
                            label={getStatusLabel(appointment.status)}
                            sx={styles.appointmentStatusChip(appointment.status)}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentMeetCell,
                          }}
                        >
                          <Tooltip
                            title={
                              meetLink
                                ? "Open Google Meet"
                                : "Meet link not available"
                            }
                          >
                            <Box component="span">
                              <IconButton
                                component={meetLink ? "a" : "button"}
                                href={meetLink || undefined}
                                target={meetLink ? "_blank" : undefined}
                                rel={meetLink ? "noopener noreferrer" : undefined}
                                disabled={!meetLink}
                                sx={styles.meetIconButton}
                                aria-label="Open Google Meet"
                              >
                                <VideocamIcon sx={{ fontSize: 19 }} />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...styles.appointmentCell,
                            ...styles.appointmentActionCell,
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={!appointment.can_cancel || isCancelling}
                            onClick={() =>
                              handleCancelSkillProviderAppointment(appointment)
                            }
                            startIcon={
                              isCancelling ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <CancelOutlinedIcon sx={{ fontSize: 16 }} />
                              )
                            }
                            sx={styles.cancelButton}
                          >
                            Cancel
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredSkillProviderAppointments.length}
              page={skillProviderAppointmentPage}
              onPageChange={(event, nextPage) =>
                setSkillProviderAppointmentPage(nextPage)
              }
              rowsPerPage={skillProviderAppointmentsRowsPerPage}
              onRowsPerPageChange={(event) => {
                setSkillProviderAppointmentsRowsPerPage(Number(event.target.value));
                setSkillProviderAppointmentPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Rows"
              labelDisplayedRows={({ count }) => `Total - ${count}`}
              ActionsComponent={AppointmentPaginationActions}
              sx={styles.appointmentsPagination}
            />
          </>
        ) : (
          <Box sx={styles.appointmentsEmpty}>
            {skillProviderAppointmentSearch
              ? "No skill provider bookings match your search."
              : "No skill provider bookings found yet."}
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderSkillSection = ({ title, sectionKey, accent, icon, emptyText }) => {
    const items = sections?.[sectionKey] || [];
    const isSkillGrowthPlan = sectionKey === "skill_growth_plans";
    const showOwnTargetSuggestions = sectionKey === "own_targets";

    return (
      <Box sx={styles.milestoneBand}>
        <Typography sx={styles.sectionTitle}>{title}</Typography>

        {items.length ? (
          <Box sx={styles.skillGrid}>
            {items.map((item, index) => (
              <Box
                key={`${sectionKey}-${item.area}-${item.improvement}-${item.suggestion}-${index}`}
                sx={styles.skillCard(accent)}
              >
                <Box>
                  <Box sx={styles.skillBadge(accent)}>{icon}</Box>
                  <Typography sx={{ color: INK, fontSize: 15, fontWeight: 700 }}>
                    Skill {index + 1}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={styles.skillName}>
                    {getDisplayLabel(item, sectionKey)}
                  </Typography>
                  <Typography sx={styles.skillMeta}>
                    {getMetaLabel(item, sectionKey)}
                  </Typography>
                  {showOwnTargetSuggestions && getOwnTargetSuggestionLabel(item) ? (
                    <Typography sx={styles.ownTargetSuggestion}>
                      Suggestion: {getOwnTargetSuggestionLabel(item)}
                    </Typography>
                  ) : null}
                </Box>

                <Typography sx={styles.targetText}>
                  Target Completion: {TARGET_YEAR}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={styles.emptyState}>
            <Typography sx={{ color: MUTED, fontSize: 13, lineHeight: 1.6 }}>
              {emptyText}
            </Typography>
          </Box>
        )}

        {isSkillGrowthPlan ? (
          <Box sx={{ mt: 2.6, display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              startIcon={<SchoolIcon sx={{ fontSize: 17 }} />}
              sx={styles.skillProviderButton}
              onClick={() => navigate("/career-skills")}
            >
              Talk to skill provider
            </Button>
          </Box>
        ) : SECTIONS_WITH_ADD_SKILL.has(sectionKey) ? (
          <Box sx={{ mt: 2.6, display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              disabled
              startIcon={<AddIcon />}
              sx={styles.primaryButton}
            >
              Add a new skill
            </Button>
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <LoggedInLayout>
      <Box sx={styles.page}>
        <Box sx={styles.content}>
          {childSelector}
          <Box sx={styles.reportBar}>
            <Box sx={styles.reportIcon}>
              <FlagIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ color: INK, fontSize: 15, fontWeight: 600 }}>
              {studentName
                ? `${studentName}'s Milestones`
                : "Assessment Report Cards"}
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
              <CircularProgress sx={{ color: NAVY }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ mt: 3 }}>
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={fetchMilestone}
                  >
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            </Box>
          )}

          {!loading && !error && (
            <>
              <Box sx={{ mt: 4 }}>
                <Typography sx={styles.sectionTitle}>
                  Career Options Recommended
                </Typography>
                {careerOptions.length ? (
                  renderCareerCards()
                ) : (
                  <Box sx={styles.emptyState}>
                    <Typography sx={{ color: MUTED, fontSize: 13 }}>
                      No recommended career options found.
                    </Typography>
                  </Box>
                )}
              </Box>
              {selectedCareer && (
                <Box sx={{ mt: 3 }}>
                  <Chip
                    label={`Selected: ${selectedCareer.career_label || selectedCareer.career_choice}`}
                    sx={{
                      bgcolor: "rgba(247, 170, 71, 0.16)",
                      color: NAVY,
                      border: "1px solid rgba(247, 170, 71, 0.45)",
                      fontWeight: 700,
                    }}
                  />
                </Box>
              )}
              <Button
                type="button"
                sx={styles.counsellorCta}
                onClick={() => navigate("/counseling")}
              >
                <Box aria-hidden="true" sx={styles.counsellorIconWrap}>
                  <NotificationsNoneIcon sx={styles.counsellorBellIcon} />
                  <Box sx={styles.counsellorPlus}>+</Box>
                </Box>
                <Typography component="span" sx={styles.counsellorText}>
                  Talk to Counsellor for Career Progress
                </Typography>
              </Button>

              {renderAppointmentsTable()}
              {renderSkillProviderAppointmentsTable()}

              <Box sx={styles.assessmentBand}>
                <Box
                  aria-hidden="true"
                  sx={styles.sparkle({
                    size: { xs: 58, sm: 74 },
                    left: { xs: 20, sm: 38 },
                    top: { xs: 13, sm: 9 },
                  })}
                />
                <Box
                  aria-hidden="true"
                  sx={styles.sparkle({
                    size: { xs: 42, sm: 52 },
                    left: { xs: -1, sm: 5 },
                    top: { xs: 7, sm: 11 },
                    opacity: 0.45,
                  })}
                />
                <Box
                  aria-hidden="true"
                  sx={styles.sparkle({
                    size: { xs: 55, sm: 70 },
                    right: { xs: -8, sm: 7 },
                    top: { xs: 61, sm: 58 },
                    opacity: 0.76,
                  })}
                />
                
                <Box
                  aria-hidden="true"
                  sx={styles.sparkle({
                    size: { xs: 39, sm: 49 },
                    right: { xs: 30, sm: 48 },
                    top: { xs: 34, sm: 34 },
                    opacity: 0.85,
                  })}
                />

                <Box sx={styles.assessmentContent}>
                  <Box aria-hidden="true" sx={styles.assessmentMark}>
                    <AssessmentBannerIcon />
                  </Box>

                  <Box sx={styles.assessmentCopy}>
                    <Typography sx={styles.assessmentLabel}>
                      Next Assessment Date :
                    </Typography>
                    <Typography sx={styles.assessmentDate}>
                      {nextAssessmentDate}
                    </Typography>
                    <Button
                      variant="contained"
                      sx={styles.assessmentReminderButton}
                    >
                      Set reminder
                    </Button>
                  </Box>
                </Box>
              </Box>

              {renderSkillSection({
                title: "Skill Growth Plans",
                sectionKey: "skill_growth_plans",
                accent: ORANGE,
                icon: <AutoAwesomeIcon sx={{ fontSize: 20 }} />,
                emptyText: "No skill growth plans are available for this career option.",
              })}

             

              {renderSkillSection({
                title: "Talk to Career Buddy",
                sectionKey: "talk_to_career_buddy",
                accent: NAVY,
                icon: <PsychologyAltIcon sx={{ fontSize: 20 }} />,
                emptyText: "No Career Buddy topics are available for this career option.",
              })}

              {renderSkillSection({
                title: "Own Targets",
                sectionKey: "own_targets",
                accent: "#0EA144",
                icon: <FlagIcon sx={{ fontSize: 19 }} />,
                emptyText: "No own targets are available for this career option.",
              })}

              <Divider sx={{ my: 4, borderColor: BORDER }} />
            </>
          )}
        </Box>
      </Box>
    </LoggedInLayout>
  );
};

Milestone.propTypes = {
  studentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studentName: PropTypes.string,
  childSelector: PropTypes.node,
};

export default Milestone;
